import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { toast } from "sonner";
import {
  ArrowLeft,
  Save,
  Package,
  Tag,
  Globe,
  BarChart3,
  Settings,
  Image as ImageIcon,
  Plus,
  X,
} from "lucide-react";
import api from "@/utils/api";
import { isAxiosError } from "axios";
import { useCurrentStore } from "@/hooks/useCurrentStore";
import { useAuth } from "@/contexts/AuthContext";
import { getCurrencySymbol } from "@/utils/currency";
import { CreateCategoryDialog } from "./CreateCategoryDialog";
import {
  ProductImageUploader,
  ProductImageAsset,
} from "./ProductImageUploader";
import { ProductFormShell } from "./ProductFormShell";
import { cn } from "@/lib/utils";
import { useProductImageSuggestions } from "@/hooks/useProductImageSuggestions";

const createProductSchema = z
  .object({
  name: z.string().min(2, "Product name must be at least 2 characters"),
  description: z.string().min(10, "Description must be at least 10 characters"),
  basePrice: z.number().min(0, "Price must be greater than 0"),
  compareAtPrice: z.number().optional().nullable(),
  category: z.string().min(1, "Category is required"),
  sku: z.string().optional(),
  weight: z.number().optional().nullable(),
  dimensions: z
    .object({
      length: z.number().optional().nullable(),
      width: z.number().optional().nullable(),
      height: z.number().optional().nullable(),
      unit: z.enum(["cm", "in"]).default("cm"),
    })
    .optional(),
  lowStockThreshold: z.number().min(0).default(5),
  options: z
    .array(
      z.object({
        name: z.string().min(1, "Option name is required"),
        required: z.boolean().default(false),
        values: z.array(
          z.object({
            name: z.string().min(1, "Value name is required"),
            price_adjustment: z.number().default(0),
          })
        ),
      })
    )
    .default([]),
  seo: z
    .object({
      title: z.string().optional(),
      description: z.string().optional(),
      keywords: z.array(z.string()).default([]),
    })
    .default({}),
  status: z.enum(["draft", "active"]).default("draft"),
  tags: z.array(z.string()).default([]),
  isDigital: z.boolean().default(false),
  shippingRequired: z.boolean().default(true),
  taxable: z.boolean().default(true),
  featured: z.boolean().default(false),
  vendor: z.string().optional(),
  wholesaleEnabled: z.boolean().default(false),
  wholesaleMinQuantity: z.number().min(2).optional().nullable(),
  wholesaleTierPrice: z.number().min(0).optional().nullable(),
})
  .superRefine((data, ctx) => {
    if (!data.wholesaleEnabled) return;
    if (data.wholesaleMinQuantity == null || data.wholesaleMinQuantity < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Minimum order quantity must be at least 2",
        path: ["wholesaleMinQuantity"],
      });
    }
    if (data.wholesaleTierPrice == null || data.wholesaleTierPrice <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Enter the total price for that minimum quantity",
        path: ["wholesaleTierPrice"],
      });
    }
  });

type CreateProductFormData = z.infer<typeof createProductSchema>;

export function CreateProductForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { storeId } = useCurrentStore();
  const { currentStore } = useAuth();
  const [productImages, setProductImages] = useState<ProductImageAsset[]>([]);
  const [newTag, setNewTag] = useState("");
  const [newKeyword, setNewKeyword] = useState("");
  const [hasVariants, setHasVariants] = useState(false);
  const [optionsState, setOptionsState] = useState<
    { name: string; values: string }[]
  >([]);
  const [variantsState, setVariantsState] = useState<
    Array<{
      options: Record<string, string>;
      price: number;
      sku?: string;
      inventory: number;
      wholesaleMinQuantity?: number | null;
      wholesaleTierPrice?: number | null;
    }>
  >([]);
  const [defaultInventory, setDefaultInventory] = useState(0);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [step, setStep] = useState(0);

  const form = useForm<CreateProductFormData>({
    resolver: zodResolver(createProductSchema),
    defaultValues: {
      name: "",
      description: "",
      basePrice: 0,
      compareAtPrice: null,
      category: "",
      sku: "",
      weight: null,
      dimensions: {
        length: null,
        width: null,
        height: null,
        unit: "cm",
      },
      lowStockThreshold: 5,
      options: [],
      seo: {
        title: "",
        description: "",
        keywords: [],
      },
      status: "draft",
      tags: [],
      isDigital: false,
      shippingRequired: true,
      taxable: true,
      featured: false,
      vendor: "",
      wholesaleEnabled: false,
      wholesaleMinQuantity: null,
      wholesaleTierPrice: null,
    },
  });

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ["categories", storeId],
    queryFn: async () => {
      const { data } = await api.get("/api/categories?tree=true");
      return data;
    },
  });

  const { analyzing, handleUploadedFiles } = useProductImageSuggestions(
    form,
    categories,
  );

  const mutation = useMutation({
    mutationFn: async (data: CreateProductFormData) => {
      const payload = {
        ...data,
        images: productImages.map((image, index) => ({
          ...image,
          isDefault: index === 0 ? true : Boolean(image.isDefault),
        })),
        options: hasVariants
          ? optionsState
              .filter((o) => o.name.trim())
              .map((o) => ({
                name: o.name.trim(),
                required: true,
                values: o.values
                  .split(",")
                  .map((v) => ({
                    name: v.trim(),
                    price_adjustment: 0,
                  }))
                  .filter((v) => v.name),
              }))
          : [],
        variants: hasVariants
          ? variantsState.map((v) => ({
              options: v.options,
              price: v.price,
              sku: v.sku,
              inventory: v.inventory,
              ...(data.wholesaleEnabled &&
              v.wholesaleMinQuantity != null &&
              v.wholesaleTierPrice != null
                ? {
                    wholesaleMinQuantity: v.wholesaleMinQuantity,
                    wholesaleTierPrice: v.wholesaleTierPrice,
                  }
                : {}),
            }))
          : [
              {
                options: {},
                price: data.basePrice,
                sku: data.sku,
                inventory: defaultInventory,
              },
            ],
      };
      return api.post("/api/products", payload);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product created successfully!");
      navigate("/dashboard/products");
    },
    onError: (error: unknown) => {
      console.error("Product creation error:", error);
      if (isAxiosError(error) && error.response?.status === 403) {
        const data = error.response?.data as { code?: string; message?: string };
        if (data?.code === "PRODUCT_LIMIT") {
          toast.error(data.message || "Product limit reached. Upgrade to Premium.");
          return;
        }
      }
      toast.error("Failed to create product");
    },
  });

  const addTag = () => {
    if (newTag.trim()) {
      const currentTags = form.getValues("tags");
      if (!currentTags.includes(newTag.trim())) {
        form.setValue("tags", [...currentTags, newTag.trim()]);
      }
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove: string) => {
    const currentTags = form.getValues("tags");
    form.setValue(
      "tags",
      currentTags.filter((tag) => tag !== tagToRemove)
    );
  };

  const addKeyword = () => {
    if (newKeyword.trim()) {
      const currentKeywords = form.getValues("seo.keywords");
      if (!currentKeywords.includes(newKeyword.trim())) {
        form.setValue("seo.keywords", [...currentKeywords, newKeyword.trim()]);
      }
      setNewKeyword("");
    }
  };

  const removeKeyword = (keywordToRemove: string) => {
    const currentKeywords = form.getValues("seo.keywords");
    form.setValue(
      "seo.keywords",
      currentKeywords.filter((keyword) => keyword !== keywordToRemove)
    );
  };

  // Flatten hierarchical categories for display
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const flattenCategories = (
    cats: any[]
  ): Array<{ _id: string; name: string; level: number }> => {
    const result: Array<{ _id: string; name: string; level: number }> = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const flatten = (categories: any[], level = 0) => {
      categories.forEach((cat) => {
        result.push({ _id: cat._id, name: cat.name, level });
        if (cat.children && cat.children.length > 0) {
          flatten(cat.children, level + 1);
        }
      });
    };
    flatten(cats);
    return result;
  };

  const flatCategories = flattenCategories(categories);
  const totalVariantInventory = variantsState.reduce(
    (sum, variant) => sum + (variant.inventory || 0),
    0
  );

  const handleCategoryCreated = (categoryId: string) => {
    form.setValue("category", categoryId);
  };

  const onSubmit = (data: CreateProductFormData) => {
    mutation.mutate(data);
  };

  return (
    <ProductFormShell
      title="New product"
      step={step}
      onStepChange={setStep}
      onBack={() => navigate("/dashboard/products")}
      onSave={() => form.handleSubmit(onSubmit)()}
      saveLabel="Create product"
      saving={mutation.isPending}
      canContinue={async () => {
        if (step !== 0) return true;
        return form.trigger(["name", "description", "category", "basePrice"]);
      }}
    >
      <CreateCategoryDialog
        isOpen={showCreateCategory}
        onClose={() => setShowCreateCategory(false)}
        onCategoryCreated={handleCategoryCreated}
        existingCategories={flatCategories}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div hidden={step !== 0} className={cn(step === 0 && "space-y-8")}>
          <section className="space-y-8">
              <div className="space-y-2">
                <FormLabel>Photos</FormLabel>
                <ProductImageUploader
                  images={productImages}
                  onChange={setProductImages}
                  maxImages={8}
                  analyzing={analyzing}
                  onFilesUploaded={handleUploadedFiles}
                />
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Product Name *</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter a descriptive product name"
                            {...field}
                            className="h-10 border-gray-300 text-sm"
                          />
                        </FormControl>
                        <FormDescription>
                          This will be displayed to customers in your store
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Describe your product features, benefits, and what makes it special..."
                            rows={6}
                            {...field}
                            className="min-h-[140px] border-[#EBEBEB] text-sm"
                          />
                        </FormControl>
                        <FormDescription>
                          Help customers understand what they're buying
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category *</FormLabel>
                        <div className="flex flex-wrap gap-2">
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                            className="flex-1"
                          >
                            <FormControl>
                              <SelectTrigger className="h-10 border-gray-300 text-sm w-full">
                                <SelectValue placeholder="Choose a category for your product" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {flatCategories.length === 0 ? (
                                <SelectItem value="__none__" disabled>
                                  No categories available
                                </SelectItem>
                              ) : (
                                flatCategories.map((category) => (
                                  <SelectItem
                                    key={category._id}
                                    value={category._id}
                                  >
                                    {"— ".repeat(category.level)}
                                    {category.name}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            onClick={() => setShowCreateCategory(true)}
                            className="shrink-0 h-9 border-gray-300 text-sm"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            New
                          </Button>
                        </div>
                        <FormDescription>
                          This helps customers find your product
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Product Status</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="h-10 w-full border-[#EBEBEB] text-sm">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="draft">
                              <div className="flex flex-col items-start">
                                <span>Save as Draft</span>
                                <span className="text-xs text-gray-500">
                                  You can edit and publish later
                                </span>
                              </div>
                            </SelectItem>
                            <SelectItem value="active">
                              <div className="flex flex-col items-start">
                                <span>Publish Now</span>
                                <span className="text-xs text-gray-500">
                                  Make it visible to customers
                                </span>
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="basePrice"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Price *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <span className="absolute left-3 top-2 text-gray-500">
                                {getCurrencySymbol(currentStore)}
                              </span>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                className="pl-8 h-10 border-gray-300 text-sm"
                                {...field}
                                onChange={(e) =>
                                  field.onChange(
                                    parseFloat(e.target.value) || 0
                                  )
                                }
                              />
                            </div>
                          </FormControl>
                          <FormDescription>
                            The selling price for this product
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="compareAtPrice"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Compare at Price (Optional)</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <span className="absolute left-3 top-2 text-gray-500">
                                {getCurrencySymbol(currentStore)}
                              </span>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                className="pl-8 h-10 border-gray-300 text-sm"
                                {...field}
                                value={field.value || ""}
                                onChange={(e) =>
                                  field.onChange(
                                    e.target.value
                                      ? parseFloat(e.target.value)
                                      : null
                                  )
                                }
                              />
                            </div>
                          </FormControl>
                          <FormDescription>
                            Original price to show discounts
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4 space-y-4 bg-muted/30">
                    <FormField
                      control={form.control}
                      name="wholesaleEnabled"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between">
                          <div>
                            <FormLabel>Wholesale / bulk pricing</FormLabel>
                            <FormDescription>
                              Offer a lower unit price when customers meet a minimum
                              quantity. Set the total price for that quantity (not per
                              unit).
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    {form.watch("wholesaleEnabled") && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="wholesaleMinQuantity"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Minimum order quantity</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={2}
                                  step={1}
                                  placeholder="e.g. 12"
                                  className="h-10 border-gray-300 text-sm"
                                  value={field.value ?? ""}
                                  onChange={(e) =>
                                    field.onChange(
                                      e.target.value
                                        ? parseInt(e.target.value, 10)
                                        : null
                                    )
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="wholesaleTierPrice"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Total price for that quantity</FormLabel>
                              <FormControl>
                                <div className="relative">
                                  <span className="absolute left-3 top-2 text-gray-500">
                                    {getCurrencySymbol(currentStore)}
                                  </span>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min={0}
                                    placeholder="0.00"
                                    className="pl-8 h-10 border-gray-300 text-sm"
                                    value={field.value ?? ""}
                                    onChange={(e) =>
                                      field.onChange(
                                        e.target.value
                                          ? parseFloat(e.target.value)
                                          : null
                                      )
                                    }
                                  />
                                </div>
                              </FormControl>
                              <FormDescription>
                                Customer pays this total when buying at least the MOQ
                                (scaled proportionally for larger orders).
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name="sku"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SKU (Optional)</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., SHIRT-BLU-M"
                            {...field}
                            className="h-10 border-gray-300 text-sm"
                          />
                        </FormControl>
                        <FormDescription>
                          Stock Keeping Unit for inventory tracking
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div>
                    <FormLabel className="text-sm">Available Stock</FormLabel>
                    {hasVariants ? (
                      <>
                        <Input
                          value={totalVariantInventory}
                          readOnly
                          className="h-10 border-gray-300 text-sm bg-muted"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Managed per variant. Update quantities in the Variant
                          Settings section.
                        </p>
                      </>
                    ) : (
                      <>
                        <Input
                          type="number"
                          min={0}
                          value={defaultInventory}
                          onChange={(e) =>
                            setDefaultInventory(
                              Math.max(0, parseInt(e.target.value || "0"))
                            )
                          }
                          className="h-10 border-gray-300 text-sm"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Customers will only be able to purchase up to this
                          quantity.
                        </p>
                      </>
                    )}
                  </div>
                  <div className="space-y-2">
                    <FormLabel>Tags (Optional)</FormLabel>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {form.watch("tags").map((tag, index) => (
                        <Badge
                          key={index}
                          variant="secondary"
                          className="flex items-center gap-1"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                          >
                            ×
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a tag (e.g., summer, bestseller)"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) =>
                          e.key === "Enter" && (e.preventDefault(), addTag())
                        }
                        className="h-10 border-gray-300 text-sm"
                      />
                      <Button
                        type="button"
                        onClick={addTag}
                        size="sm"
                        className="h-10 text-sm"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Tags help customers discover your products
                    </p>
                  </div>
                </div>
              </div>
          </section>
          </div>

          <div hidden={step !== 2} className={cn(step === 2 && "space-y-8")}>
          <section className="space-y-5">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-primary">
                Product Attributes
            </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="vendor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vendor</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Vendor name"
                          {...field}
                          className="h-10 border-gray-300 text-sm"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="weight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Weight (kg)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseFloat(e.target.value) : null
                            )
                          }
                          className="h-10 border-gray-300 text-sm"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="dimensions.length"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Length</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                          value={field.value || ""}
                          className="h-10 border-gray-300 text-sm"
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseFloat(e.target.value) : null
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dimensions.width"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Width</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                          value={field.value || ""}
                          className="h-10 border-gray-300 text-sm"
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseFloat(e.target.value) : null
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dimensions.height"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Height</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                          value={field.value || ""}
                          className="h-10 border-gray-300 text-sm"
                          onChange={(e) =>
                            field.onChange(
                              e.target.value ? parseFloat(e.target.value) : null
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dimensions.unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-10 border-gray-300 text-sm">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="cm">cm</SelectItem>
                          <SelectItem value="in">inches</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
          </section>
          </div>

          <div hidden={step !== 1} className={cn(step === 1 && "space-y-8")}>
          <section className="space-y-5">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-primary">
                Variant Settings
            </h2>
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-2">
                <div className="space-y-0.5">
                  <FormLabel className="text-sm">
                    This product has variants
                  </FormLabel>
                  <FormDescription className="text-xs">
                    Enable variants to manage options and per-variant inventory
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={hasVariants}
                    onCheckedChange={(checked) => {
                      setHasVariants(checked);
                      if (!checked) {
                        setOptionsState([]);
                        setVariantsState([]);
                      }
                    }}
                  />
                </FormControl>
              </FormItem>

              {!hasVariants && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <FormLabel className="text-sm">
                      Default Variant SKU
                    </FormLabel>
                    <Input
                      value={form.watch("sku") || ""}
                      onChange={(e) => form.setValue("sku", e.target.value)}
                      placeholder="e.g. PROD-001"
                      className="h-10 border-gray-300 text-sm"
                    />
                  </div>
                  <div>
                    <FormLabel className="text-sm">Default Price</FormLabel>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.watch("basePrice") || 0}
                      onChange={(e) =>
                        form.setValue(
                          "basePrice",
                          parseFloat(e.target.value) || 0
                        )
                      }
                      className="h-10 border-gray-300 text-sm"
                    />
                  </div>
                </div>
              )}

              {hasVariants && (
                <>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">
                        Define option groups (e.g., Size, Color)
                      </Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setOptionsState([
                            ...optionsState,
                            { name: "", values: "" },
                          ])
                        }
                      >
                        Add Option
                      </Button>
                    </div>
                    <div className="space-y-3">
                      {optionsState.map((opt, idx) => (
                        <div
                          key={idx}
                          className="grid grid-cols-1 md:grid-cols-2 gap-3"
                        >
                          <Input
                            placeholder="Option name"
                            value={opt.name}
                            onChange={(e) => {
                              const arr = [...optionsState];
                              arr[idx] = { ...arr[idx], name: e.target.value };
                              setOptionsState(arr);
                            }}
                            className="h-10 border-gray-300 text-sm"
                          />
                          <Input
                            placeholder="Comma separated values"
                            value={opt.values}
                            onChange={(e) => {
                              const arr = [...optionsState];
                              arr[idx] = {
                                ...arr[idx],
                                values: e.target.value,
                              };
                              setOptionsState(arr);
                            }}
                            className="h-10 border-gray-300 text-sm"
                          />
                        </div>
                      ))}
                      {optionsState.length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          No options defined.
                        </p>
                      )}
                      <Button
                        type="button"
                        onClick={() => {
                          const optionLists = optionsState
                            .filter((o) => o.name.trim() && o.values.trim())
                            .map((o) => ({
                              name: o.name.trim(),
                              values: o.values
                                .split(",")
                                .map((v) => v.trim())
                                .filter(Boolean),
                            }));
                          if (optionLists.length === 0) {
                            setVariantsState([]);
                            return;
                          }
                          // Cartesian product
                          const build = (
                            acc: Array<Record<string, string>>,
                            idx: number
                          ): Array<Record<string, string>> => {
                            if (idx >= optionLists.length) return acc;
                            const next: Array<Record<string, string>> = [];
                            for (const combo of acc) {
                              for (const val of optionLists[idx].values) {
                                next.push({
                                  ...combo,
                                  [optionLists[idx].name]: val,
                                });
                              }
                            }
                            return build(next, idx + 1);
                          };
                          const combos = build([{}], 0);
                          setVariantsState(
                            combos.map((c) => ({
                              options: c,
                              price: form.getValues("basePrice"),
                              sku: undefined,
                              inventory: 0,
                            }))
                          );
                        }}
                        className="h-10 border-gray-300 text-sm"
                      >
                        Generate Variants
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium">
                      Variants & Stock
                    </Label>
                    {variantsState.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        No variants yet. Generate from options above.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {variantsState.map((v, i) => (
                          <div
                            key={i}
                            className="border rounded-lg p-3 space-y-2"
                          >
                            <div className="grid min-w-0 grid-cols-1 gap-3 overflow-x-auto md:grid-cols-4">
                              <div className="text-sm">
                                {Object.entries(v.options).map(([k, val]) => (
                                  <div key={k}>
                                    <span className="text-muted-foreground">
                                      {k}:
                                    </span>{" "}
                                    {val}
                                  </div>
                                ))}
                              </div>
                              <Input
                                type="text"
                                placeholder="SKU"
                                value={v.sku || ""}
                                onChange={(e) => {
                                  const arr = [...variantsState];
                                  arr[i] = { ...arr[i], sku: e.target.value };
                                  setVariantsState(arr);
                                }}
                                className="h-10 border-gray-300 text-sm"
                              />
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="Price"
                                value={v.price}
                                onChange={(e) => {
                                  const arr = [...variantsState];
                                  arr[i] = {
                                    ...arr[i],
                                    price: parseFloat(e.target.value) || 0,
                                  };
                                  setVariantsState(arr);
                                }}
                                className="h-10 border-gray-300 text-sm"
                              />
                              <Input
                                type="number"
                                min={0}
                                placeholder="Inventory"
                                value={v.inventory}
                                onChange={(e) => {
                                  const arr = [...variantsState];
                                  arr[i] = {
                                    ...arr[i],
                                    inventory: Math.max(
                                      0,
                                      parseInt(e.target.value || "0")
                                    ),
                                  };
                                  setVariantsState(arr);
                                }}
                                className="h-10 border-gray-300 text-sm"
                              />
                            </div>
                            {form.watch("wholesaleEnabled") && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 border-t">
                                <Input
                                  type="number"
                                  min={2}
                                  step={1}
                                  placeholder="Wholesale MOQ override (optional)"
                                  value={v.wholesaleMinQuantity ?? ""}
                                  onChange={(e) => {
                                    const arr = [...variantsState];
                                    arr[i] = {
                                      ...arr[i],
                                      wholesaleMinQuantity: e.target.value
                                        ? parseInt(e.target.value, 10)
                                        : null,
                                    };
                                    setVariantsState(arr);
                                  }}
                                  className="h-9 border-gray-300 text-xs"
                                />
                                <Input
                                  type="number"
                                  step="0.01"
                                  min={0}
                                  placeholder="Wholesale tier total override (optional)"
                                  value={v.wholesaleTierPrice ?? ""}
                                  onChange={(e) => {
                                    const arr = [...variantsState];
                                    arr[i] = {
                                      ...arr[i],
                                      wholesaleTierPrice: e.target.value
                                        ? parseFloat(e.target.value)
                                        : null,
                                    };
                                    setVariantsState(arr);
                                  }}
                                  className="h-9 border-gray-300 text-xs"
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
          </section>
          </div>

          <div hidden={step !== 2} className={cn(step === 2 && "space-y-8")}>
          <section className="space-y-5">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-primary">
                SEO Settings
            </h2>
              <FormField
                control={form.control}
                name="seo.title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SEO Title</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="SEO title for search engines"
                        {...field}
                        className="h-10 border-gray-300 text-sm"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="seo.description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SEO Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Brief description for search engines"
                        rows={3}
                        {...field}
                        className="min-h-[88px] border-[#EBEBEB] text-sm"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel>SEO Keywords</FormLabel>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.watch("seo.keywords").map((keyword, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="flex items-center gap-1"
                    >
                      {keyword}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => removeKeyword(keyword)}
                      />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add keyword"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyPress={(e) =>
                      e.key === "Enter" && (e.preventDefault(), addKeyword())
                    }
                    className="h-10 border-gray-300 text-sm"
                  />
                  <Button
                    type="button"
                    onClick={addKeyword}
                    size="sm"
                    className="h-10 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
          </section>

          <section className="space-y-5">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-primary">
                Product Settings
            </h2>
              <FormField
                control={form.control}
                name="featured"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-gray-300 p-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm">
                        Featured Product
                      </FormLabel>
                      <FormDescription className="text-xs">
                        Show this product in featured sections
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isDigital"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-gray-300 p-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm">Digital Product</FormLabel>
                      <FormDescription className="text-xs">
                        This product is delivered digitally(Not a physical
                        product)
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="shippingRequired"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-gray-300 p-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm">
                        Shipping Required
                      </FormLabel>
                      <FormDescription className="text-xs">
                        This product requires shipping/delivery
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="taxable"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-gray-300 p-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm">Taxable</FormLabel>
                      <FormDescription className="text-xs">
                        Charge tax on this product
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lowStockThreshold"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Low Stock Threshold</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        placeholder="5"
                        {...field}
                        className="h-10 border-gray-300 text-sm"
                        onChange={(e) =>
                          field.onChange(parseInt(e.target.value) || 0)
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      Get notified when stock falls below this number
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
          </section>
          </div>
        </form>
      </Form>
    </ProductFormShell>
  );
}
